import type {
    VirtualAppointmentDto,
    VirtualAppointmentTriggerKind,
    VirtualAppointmentWeeksFrom
} from '../../services/virtualAppointments';
import {
    NEXT_SERIES_COUNTS_FROM_TREATMENT_START,
    NO_TREATMENT_START_ON_RECORD
} from './virtualAppointmentWeeksHint.ts';
import { DEFAULT_WEEKS_FROM, weeksFromOf } from './virtualAppointmentWeeks.ts';

export interface SavedTrigger {
    triggerKind: VirtualAppointmentTriggerKind;
    triggerAligner: number | null;
    triggerWeeks: number | null;
    triggerWeeksFrom: string | null;
}

export type SavedTriggers = ReadonlyMap<string, SavedTrigger>;

export interface ParsedTrigger {
    /** Absent for a VA not saved yet. */
    id?: string;
    triggerKind: VirtualAppointmentTriggerKind;
    aligner: number | null;
    weeks: number | null;
    /** Absent counts from the treatment start, as on the wire. */
    weeksFrom?: VirtualAppointmentWeeksFrom;
}

export interface AlignerLimit {
    total: number;
}

export interface TriggerProgress {
    /** Null when unknown, which allows any aligner. */
    currentAligner: number | null;
    /** Null without a Treatment-phase start, which allows any week. */
    currentWeek: number | null;
    /** Null when the aligner count is unknown, which allows any aligner. */
    alignerLimit: AlignerLimit | null;
    /** In Treatment with no start on record, a week counted from the treatment start never fires. */
    weeksUnavailable?: boolean;
}

export const NO_SAVED_TRIGGERS: SavedTriggers = new Map();

export function savedTriggersOf(vas: readonly VirtualAppointmentDto[] | undefined): SavedTriggers {
    return new Map(
        (vas ?? [])
            .filter((va) => !va.frozen)
            .map((va) => [
                va.id,
                {
                    triggerKind: va.triggerKind,
                    triggerAligner: va.triggerAligner,
                    triggerWeeks: va.triggerWeeks,
                    triggerWeeksFrom: va.triggerWeeksFrom
                }
            ])
    );
}

// Mirrors the backend's "newly written" rule; a saved VA with no known baseline counts as unchanged.
export function isTriggerWritten(trigger: ParsedTrigger, saved: SavedTriggers): boolean {
    if (trigger.id === undefined) return true;
    const stored = saved.get(trigger.id);
    if (!stored) return false;
    if (stored.triggerKind !== trigger.triggerKind) return true;
    switch (trigger.triggerKind) {
        case 'aligner':
            return stored.triggerAligner !== trigger.aligner;
        case 'weeks':
            return (
                stored.triggerWeeks !== trigger.weeks ||
                weeksFromOf(stored.triggerWeeksFrom) !== (trigger.weeksFrom ?? DEFAULT_WEEKS_FROM)
            );
        case 'nogos':
        case 'now':
            return false;
    }
}

// The API keeps a "from now" anchor while the kind and week stay as saved, and re-stamps otherwise.
export function keptWeeksAnchor(trigger: ParsedTrigger, saved: SavedTriggers): string | null {
    if (trigger.id === undefined || trigger.triggerKind !== 'weeks') return null;
    if (trigger.weeksFrom !== 'now') return null;
    const stored = saved.get(trigger.id);
    if (stored?.triggerKind !== 'weeks' || stored.triggerWeeks !== trigger.weeks) return null;
    return stored.triggerWeeksFrom;
}

// Like the API, only a "from now" week being written is refused, so a kept anchor still saves.
export function nextSeriesWeeksFromError(
    trigger: ParsedTrigger,
    weeksFromNowAllowed: boolean,
    saved: SavedTriggers
): string | undefined {
    if (weeksFromNowAllowed || trigger.triggerKind !== 'weeks') return undefined;
    if (trigger.weeksFrom !== 'now' || !isTriggerWritten(trigger, saved)) return undefined;
    return NEXT_SERIES_COUNTS_FROM_TREATMENT_START;
}

export function alignerPassedError(
    aligner: number,
    currentAligner: number | null
): string | undefined {
    return currentAligner !== null && aligner <= currentAligner
        ? `Pick an aligner after the current one (${currentAligner})`
        : undefined;
}

// An unknown or zero count sets no limit.
export function alignerLimitOf(total: number | null | undefined): AlignerLimit | null {
    return total != null && Number.isInteger(total) && total > 0 ? { total } : null;
}

function alignerCount(total: number): string {
    return total === 1 ? '1 aligner' : `${total} aligners`;
}

export function alignerOverLimitError(
    aligner: number,
    limit: AlignerLimit | null
): string | undefined {
    if (limit === null || aligner <= limit.total) return undefined;
    return `This patient has ${alignerCount(limit.total)}`;
}

export function weekPassedError(week: number, currentWeek: number | null): string | undefined {
    return currentWeek !== null && week <= currentWeek
        ? `Week ${week} has passed (current week ${currentWeek})`
        : undefined;
}

// A week counted from now is always ahead; only one counted from the treatment start can be out of reach.
function weeksProgressError(trigger: ParsedTrigger, progress: TriggerProgress): string | undefined {
    if ((trigger.weeksFrom ?? DEFAULT_WEEKS_FROM) !== 'treatment_start') return undefined;
    if (progress.weeksUnavailable) return NO_TREATMENT_START_ON_RECORD;
    return trigger.weeks === null
        ? undefined
        : weekPassedError(trigger.weeks, progress.currentWeek);
}

// Only a trigger being written is checked, so a saved VA already out of reach still saves as is.
export function triggerProgressError(
    trigger: ParsedTrigger,
    progress: TriggerProgress,
    saved: SavedTriggers
): string | undefined {
    if (!isTriggerWritten(trigger, saved)) return undefined;
    if (trigger.triggerKind === 'aligner' && trigger.aligner !== null) {
        return (
            alignerPassedError(trigger.aligner, progress.currentAligner) ??
            alignerOverLimitError(trigger.aligner, progress.alignerLimit)
        );
    }
    return trigger.triggerKind === 'weeks' ? weeksProgressError(trigger, progress) : undefined;
}
